import {
  QUERIES_ADMIN,
  useSaleOrderDeleteSupplyOrderVinculed,
  useSaleOrderSupplyOrders,
} from '@/services'

import { CardSupplyOrder } from './components'

import { TabSuplierProps } from './tab-supplier.type'

import { EmptyState } from '@/components'
import { onConfirmInfoMessage } from '@/helpers'
import { showToast } from '@/lib'
import { ROUTES_ADMIN } from '@/router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CardShowTotal } from './components/card-show-total'
import styles from './tab-supplier.module.css'

export const TabSuplier = ({
  saleOrderUuid,
  saleOrderReference,
  supplierUuid,
  saleOrderDestinationAddress,
  isLoading,
}: TabSuplierProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { data, isLoading: isLoadingSupplyOrders } =
    useSaleOrderSupplyOrders(saleOrderUuid)

  const { mutateAsync: deleteSupplyOrderMutateAsync } =
    useSaleOrderDeleteSupplyOrderVinculed()

  const isLoadingAll = isLoading || isLoadingSupplyOrders

  const handleDeleteSupplyOrder = async (supplyOrderUuid: string) => {
    const confirm = await onConfirmInfoMessage(
      t('orderCenterShowTabSupplierDeleteConfirmTitle'),
      t('orderCenterShowTabSupplierDeleteConfirmMessage'),
      t('orderCenterShowTabSupplierDeleteConfirmYesButton'),
      t('orderCenterShowTabSupplierDeleteConfirmNoButton'),
    )

    if (!confirm) return

    const response = await deleteSupplyOrderMutateAsync({
      uuid: saleOrderUuid || '',
      supplyOrderUuid,
    })

    if (!response?.success) {
      return showToast({
        variant: 'error',
        title: t('orderCenterShowTabSupplierDeleteErrorToastMessage'),
      })
    }

    queryClient.refetchQueries({
      queryKey: [
        QUERIES_ADMIN.BFF_SALE_ORDER_SUPPLY_ORDERS_URL.module,
        saleOrderUuid,
      ],
    })

    showToast({
      variant: 'success',
      title: t('orderCenterShowTabSupplierDeleteSuccessToastMessage'),
    })
  }

  const goToCreateSupplyOrder = () => {
    navigate(
      `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.CREATE.fullPath()}/?references=${saleOrderReference}&supplierUuid=${supplierUuid}`,
    )
  }

  if (
    !isLoadingAll &&
    (!data?.data?.supplyOrders?.length ||
      data?.data?.supplyOrders?.length === 0)
  ) {
    return (
      <EmptyState
        title={t('orderCenterShowTabSupplierEmptyTitle')}
        description={t('orderCenterShowTabSupplierEmptyDescription')}
        buttonText={t('orderCenterShowTabSupplierCreateSupplyOrderButton')}
        onClickButton={goToCreateSupplyOrder}
      />
    )
  }

  return (
    <>
      <div className={styles.container}>
        {!isLoadingAll ? (
          data?.data?.supplyOrders?.map((supplyOrder) => (
            <CardSupplyOrder
              key={supplyOrder?.uuid}
              supplyOrder={supplyOrder}
              saleOrderDestinationAddress={saleOrderDestinationAddress}
              onRemove={handleDeleteSupplyOrder}
            />
          ))
        ) : (
          <CardSupplyOrder isLoading />
        )}

        <CardShowTotal
          totalWithoutIva={data?.data?.totalWithoutIva}
          total={data?.data?.total}
          iva={data?.data?.iva}
          dollarTotalWithoutIva={data?.data?.dollarTotalWithoutIva}
          dollarTotal={data?.data?.dollarTotal}
          dollarIva={data?.data?.dollarIva}
          isLoading={isLoadingAll}
        />
      </div>
    </>
  )
}
